import React, { useEffect, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { ACHIEVEMENTS, getAchievementAssets } from '../../utils/achievementsData';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useTranslation } from 'react-i18next';
import { X, Share2, Check } from 'lucide-react';
import { useHaptic } from '../../hooks/useHaptic';
import ShareCard from '../common/ShareCardGenerator';

const AchievementModalManager = () => {
    const { profile, user } = useUserData();
    const { t } = useTranslation();
    const [activeAchievement, setActiveAchievement] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showShareCard, setShowShareCard] = useState(false);
    const { notification } = useHaptic();

    useEffect(() => {
        if (!profile?.achievements) {
            setIsOpen(false);
            return;
        }

        // Find the first achievement that is an object AND unseen
        // (Handling potential legacy strings by ignoring them or assuming they are seen)
        const unseenEntry = profile.achievements.find(a => typeof a === 'object' && !a.seen);

        if (unseenEntry) {
            const achDef = ACHIEVEMENTS.find(def => def.id === unseenEntry.id);
            if (achDef) {
                setActiveAchievement(achDef);
                setIsOpen(true);
                notification('success');
            } else {
                // If ID is valid but not found in definition (removed?), mark seen to unblock
                markAsSeen(unseenEntry.id);
            }
        } else {
            setIsOpen(false);
            setTimeout(() => setActiveAchievement(null), 300); // Clear after anim
        }
    }, [profile]);

    const markAsSeen = async (id) => {
        if (!user || !profile) return;

        try {
            const updatedList = profile.achievements.map(a => {
                // If legacy string, leave as is (or auto-migrate if we wanted, but safer to leave)
                if (typeof a !== 'object') return a;

                if (a.id === id) {
                    return { ...a, seen: true };
                }
                return a;
            });

            await updateDoc(doc(db, 'users', user.uid), { achievements: updatedList });
        } catch (error) {
            console.error("Error marking achievement as seen:", error);
        }
    };

    const handleClose = () => {
        if (activeAchievement) {
            markAsSeen(activeAchievement.id);
        }
    };

    if (!activeAchievement && !isOpen) return null;

    const { frame, icon } = getAchievementAssets(activeAchievement?.rarity, activeAchievement?.type);

    // Rarity colors for text accents
    const getRarityGlow = (r) => {
        switch (r) {
            case 'legendary': return 'from-blue-500/50 via-purple-500/50 to-blue-500/50';
            case 'epic': return 'from-yellow-500/50 via-orange-500/50 to-yellow-500/50';
            case 'rare': return 'from-blue-400/50 via-cyan-400/50 to-blue-400/50';
            default: return 'from-neutral-700/50 via-neutral-600/50 to-neutral-700/50';
        }
    };

    const getTextColor = (r) => {
        switch (r) {
            case 'legendary': return 'text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 animate-pulse';
            case 'epic': return 'text-yellow-400';
            case 'rare': return 'text-blue-200';
            default: return 'text-neutral-300';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1000000] flex items-center justify-center p-6 bg-black/95 backdrop-blur-xl animate-in fade-in duration-300">

            {/* Ambient Background Glow - Static to avoid distractions */}
            <div className={`absolute inset-0 bg-gradient-to-tr ${getRarityGlow(activeAchievement?.rarity)} opacity-10`} />

            {/* Main Card Container */}
            <div className={`relative max-w-sm w-full bg-neutral-900 overflow-hidden rounded-3xl border border-white/10 shadow-2xl flex flex-col items-center p-8 animate-in zoom-in-95 duration-500`}>

                {/* Gradient Border Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${getRarityGlow(activeAchievement?.rarity)} opacity-20 pointer-events-none`} />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-900/50 to-neutral-900 pointer-events-none" />

                {/* Close Button (Optional, but good UX) */}
                <button onClick={handleClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-neutral-500 transition-colors z-50">
                    <X size={20} />
                </button>

                {/* Visual Assembly */}
                <div className="relative w-48 h-48 mb-8 flex items-center justify-center z-10">
                    {/* Back Glow - Static */}
                    <div className={`absolute inset-0 blur-3xl opacity-30 bg-gradient-to-tr ${getRarityGlow(activeAchievement?.rarity)}`} />

                    {/* Faint Radial Highlight behind icon */}
                    <div className="absolute inset-0 bg-white/5 rounded-full blur-2xl" />

                    {/* Frame */}
                    <img
                        src={frame}
                        alt="Frame"
                        className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl animate-in zoom-in duration-500"
                        style={{
                            maskImage: 'radial-gradient(circle, black 60%, transparent 70%)',
                            WebkitMaskImage: 'radial-gradient(circle, black 60%, transparent 70%)'
                        }}
                    />

                    {/* Icon */}
                    <img
                        src={icon}
                        alt="Icon"
                        className="absolute inset-[22%] w-[56%] h-[56%] object-contain drop-shadow-lg mix-blend-screen animate-in zoom-in slide-in-from-bottom-5 duration-700 delay-150"
                    />
                </div>

                {/* Text Content */}
                <div className="text-center space-y-3 mb-8 z-10 animate-in slide-in-from-bottom-4 fade-in duration-700 delay-300">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                        <span className="text-[10px] font-black tracking-[0.2em] text-white uppercase">
                            {t('achievements.unlocked_msg', '¡LOGRO DESBLOQUEADO!')}
                        </span>
                    </div>

                    <h1 className={`text-3xl font-black italic uppercase tracking-wider leading-none ${getTextColor(activeAchievement?.rarity)} drop-shadow-lg`}>
                        {t(activeAchievement?.titleKey)}
                    </h1>

                    <p className="text-neutral-400 text-sm font-medium leading-relaxed px-4">
                        {t(activeAchievement?.descKey)}
                    </p>
                </div>

                {/* Action Button */}
                <div className="w-full z-10 animate-in slide-in-from-bottom-4 fade-in duration-700 delay-500">
                    <button
                        onClick={handleClose}
                        className="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-black italic uppercase tracking-wider rounded-xl shadow-[0_0_30px_-10px_rgba(220,38,38,0.6)] hover:shadow-[0_0_40px_-5px_rgba(220,38,38,0.8)] transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                        <span>{t('common.awesome', '¡A seguir así!')}</span>
                        <Check size={18} strokeWidth={3} />
                    </button>

                    <button
                        onClick={() => setShowShareCard(true)}
                        className="w-full mt-3 py-4 bg-neutral-800 hover:bg-neutral-700 text-white font-black italic uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        <Share2 size={18} />
                        <span>{t('common.share', 'Compartir')}</span>
                    </button>

                    <p className="text-[10px] text-neutral-600 text-center mt-4 font-bold uppercase tracking-widest cursor-pointer hover:text-neutral-400 transition-colors" onClick={handleClose}>
                        Cerrar
                    </p>
                </div>

            </div>

            {showShareCard && (
                <ShareCard
                    type="achievement"
                    data={{
                        title: t(activeAchievement?.titleKey),
                        description: t(activeAchievement?.descKey),
                        icon: icon // Using the icon from getAchievementAssets
                    }}
                    onClose={() => setShowShareCard(false)}
                />
            )}
        </div>
    );
};

export default AchievementModalManager;
